import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, CheckCircle2 } from 'lucide-react';
import { KIND_ICON, SEVERITY_COLOR, timeAgo, type Notification } from '../components/NotificationBell';

// Tutte le notifiche (bottone "Open all notifications" della campanella, 26/10/2026): aperte e
// chiuse degli ultimi 30 giorni (GET /api/notifications?all=1), filtrabili per stato.
type Filter = 'open' | 'resolved' | 'all';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('open');

  async function load() {
    const res = await axios.get('/api/notifications', { params: { all: 1 } });
    setItems(res.data.data);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function markRead(ids?: number[]) {
    await axios.post('/api/notifications/read', ids ? { ids } : {});
    setItems(list => list.map(n => (!ids || ids.includes(n.id) ? { ...n, read: true } : n)));
  }

  function openItem(n: Notification) {
    if (!n.read) markRead([n.id]);
    if (n.link) navigate(n.link);
  }

  const shown = items.filter(n => filter === 'all' || (filter === 'open' ? !n.resolved_at : !!n.resolved_at));
  const counts = { open: items.filter(n => !n.resolved_at).length, resolved: items.filter(n => n.resolved_at).length, all: items.length };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2"><Bell size={20} /> Notifications</h1>
        <button onClick={() => markRead()} className="flex items-center gap-1 text-sm text-primary-600 hover:underline">
          <CheckCheck size={14} /> Mark all read
        </button>
      </div>

      <div className="flex gap-1 mb-3">
        {(['open', 'resolved', 'all'] as Filter[]).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${filter === f ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {f === 'open' ? 'Open' : f === 'resolved' ? 'Resolved' : 'All'} ({counts[f]})
          </button>
        ))}
      </div>

      <div className="card divide-y divide-gray-100">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">Nothing to report</p>
        ) : shown.map(n => (
          <div key={n.id} onClick={() => openItem(n)}
            className={`px-4 py-3 flex items-start gap-3 ${n.link ? 'cursor-pointer hover:bg-gray-50' : ''} ${!n.read && !n.resolved_at ? 'bg-blue-50/50' : ''}`}>
            <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${n.resolved_at ? 'bg-emerald-100 text-emerald-600' : SEVERITY_COLOR[n.severity]}`}>
              {n.resolved_at ? <CheckCircle2 size={14} /> : KIND_ICON[n.kind]}
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm ${!n.read && !n.resolved_at ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{n.title}</p>
              <p className="text-sm text-gray-600 mt-0.5">{n.message}</p>
              <p className="text-xs text-gray-400 mt-1">
                {n.tenant_name && <>{n.tenant_name} · </>}
                {new Date(n.created_at).toLocaleString('en-GB')} ({timeAgo(n.created_at)})
                {n.resolved_at && <> · resolved {timeAgo(n.resolved_at)}</>}
              </p>
            </div>
            {!n.read && !n.resolved_at && <span className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />}
          </div>
        ))}
      </div>
    </div>
  );
}
